import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import client from '../api/client';

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

const STATUSES = ['pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled'];

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) navigate('/');
  }, [user, authLoading, navigate]);

  if (authLoading || !user || user.role !== 'admin') return null;

  return (
    <>
      <header style={{ background: 'var(--ink)', color: 'var(--cream)', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ fontFamily: 'var(--font-display)', fontWeight: '700', color: 'var(--cream)' }}>FMB Admin</Link>
        <Link to="/" className="mono" style={{ color: 'var(--warm-gray)' }}>Back to Site</Link>
      </header>

      <div className="admin-layout">
        <aside className="admin-sidebar">
          {['orders', 'products', 'customers', 'stats'].map(t => (
            <button key={t} className={`admin-nav-item ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </aside>

        <div className="admin-content">
          {tab === 'orders' && <OrdersTab />}
          {tab === 'products' && <ProductsTab />}
          {tab === 'customers' && <CustomersTab />}
          {tab === 'stats' && <StatsTab />}
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════
   ORDERS TAB
   ════════════════════════════════════════════════════ */
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    client.get('/api/admin/orders', { params })
      .then(res => setOrders(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter, search]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateOrder = async (orderNumber, data) => {
    const res = await client.patch(`/api/admin/orders/${orderNumber}`, data);
    setOrders(prev => prev.map(o => o.order_number === orderNumber ? res.data : o));
    if (detail?.order_number === orderNumber) setDetail(res.data);
  };

  const bulkUpdate = async (status) => {
    if (!selected.size) return;
    await client.post('/api/admin/orders/bulk-status', { order_numbers: [...selected], status });
    setSelected(new Set());
    fetchOrders();
  };

  const toggleSelect = (num) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(num) ? next.delete(num) : next.add(num);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === orders.length) setSelected(new Set());
    else setSelected(new Set(orders.map(o => o.order_number)));
  };

  // ── Order Detail View ──
  if (detail) {
    return (
      <div>
        <button onClick={() => setDetail(null)} className="mono" style={{ background: 'none', border: 'none', color: 'var(--warm-gray)', marginBottom: '24px', cursor: 'pointer' }}>
          &larr; Back to Orders
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '32px', alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <h2>{detail.order_number}</h2>
              <span className={`badge badge-${detail.status}`}>{detail.status}</span>
            </div>

            {/* Items */}
            <div className="cart-summary">
              <h4 style={{ marginBottom: '12px' }}>Items</h4>
              {detail.items?.map((item, i) => (
                <div className="cart-item" key={i}>
                  <div className="cart-item-details">
                    <div className="cart-item-name">{item.product_name}</div>
                    {(item.variant || item.color) && <div className="cart-item-variant">{item.variant || item.color}</div>}
                    <div className="cart-item-variant">Qty: {item.quantity}</div>
                  </div>
                  <div className="cart-item-price">{formatPrice(item.price_cents * item.quantity)}</div>
                </div>
              ))}
              <div className="cart-totals">
                <div className="cart-total-row"><span>Subtotal</span><span>{formatPrice(detail.subtotal_cents)}</span></div>
                <div className="cart-total-row"><span>Shipping</span><span>{detail.shipping_cents === 0 ? 'FREE' : formatPrice(detail.shipping_cents)}</span></div>
                <div className="cart-total-row grand"><span>Total</span><span>{formatPrice(detail.total_cents)}</span></div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Customer info */}
            <div className="cart-summary" style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '12px' }}>Customer</h4>
              <p style={{ fontWeight: '600' }}>{detail.customer_name}</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--warm-gray)' }}>{detail.email}</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--warm-gray)', marginTop: '4px' }}>
                {new Date(detail.created_at).toLocaleString()}
              </p>
            </div>

            {/* Shipping */}
            {detail.shipping_address && (
              <div className="cart-summary" style={{ marginBottom: '16px' }}>
                <h4 style={{ marginBottom: '8px' }}>Shipping Address</h4>
                <p style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
                  {detail.shipping_address.name}<br />
                  {detail.shipping_address.line1}<br />
                  {detail.shipping_address.line2 && <>{detail.shipping_address.line2}<br /></>}
                  {detail.shipping_address.city}, {detail.shipping_address.state} {detail.shipping_address.zip}
                </p>
              </div>
            )}

            {/* Status + Tracking */}
            <div className="cart-summary">
              <h4 style={{ marginBottom: '12px' }}>Update Order</h4>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-input" value={detail.status} onChange={e => updateOrder(detail.order_number, { status: e.target.value })}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tracking Number</label>
                <input
                  className="form-input"
                  defaultValue={detail.tracking_number || ''}
                  onBlur={e => {
                    const val = e.target.value.trim();
                    if (val !== (detail.tracking_number || '')) updateOrder(detail.order_number, { tracking_number: val });
                  }}
                  placeholder="Enter tracking #"
                />
              </div>
              {detail.stripe_payment_intent && (
                <p style={{ fontSize: '0.75rem', color: 'var(--warm-gray)', marginTop: '8px' }}>
                  <span className="mono">Stripe:</span> {detail.stripe_payment_intent}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Orders List View ──
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2>Orders</h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input className="form-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: '200px' }} />
          <select className="form-input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '130px' }}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '10px 16px', background: 'white', borderRadius: '6px' }}>
          <span className="mono" style={{ color: 'var(--warm-gray)' }}>{selected.size} selected</span>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdate('processing')}>Mark Processing</button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdate('shipped')}>Mark Shipped</button>
          <button className="btn btn-sm btn-secondary" onClick={() => bulkUpdate('delivered')}>Mark Delivered</button>
          <button className="btn btn-sm btn-secondary" onClick={() => setSelected(new Set())} style={{ marginLeft: 'auto' }}>Clear</button>
        </div>
      )}

      {loading ? <p>Loading...</p> : orders.length === 0 ? <p style={{ color: 'var(--warm-gray)' }}>No orders found.</p> : (
        <table className="data-table">
          <thead>
            <tr>
              <th><input type="checkbox" onChange={toggleAll} checked={selected.size === orders.length && orders.length > 0} /></th>
              <th>Order</th><th>Customer</th><th>Date</th><th>Total</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.order_number}>
                <td><input type="checkbox" checked={selected.has(o.order_number)} onChange={() => toggleSelect(o.order_number)} /></td>
                <td><strong>{o.order_number}</strong></td>
                <td>
                  <div>{o.customer_name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--warm-gray)' }}>{o.email}</div>
                </td>
                <td>{new Date(o.created_at).toLocaleDateString()}</td>
                <td>{formatPrice(o.total_cents)}</td>
                <td><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => setDetail(o)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════
   PRODUCTS TAB
   ════════════════════════════════════════════════════ */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or 'new'
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    client.get('/api/admin/products')
      .then(res => setProducts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const startEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name,
      slug: product.slug,
      description: product.description || '',
      price_cents: product.price_cents,
      compare_price_cents: product.compare_price_cents || '',
      category: product.category || 'rack',
      featured: product.featured || false,
      sort_order: product.sort_order || 0,
      colors: (product.options?.colors || []).join(', '),
    });
  };

  const startNew = () => {
    setEditing('new');
    setForm({
      name: '', slug: '', description: '',
      price_cents: '', compare_price_cents: '',
      category: 'rack', featured: false, sort_order: 0,
      colors: 'red, blue, cream, ink, gold, forest',
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const colors = form.colors ? form.colors.split(',').map(c => c.trim()).filter(Boolean) : [];
    const payload = {
      name: form.name,
      description: form.description,
      price_cents: parseInt(form.price_cents),
      compare_price_cents: form.compare_price_cents ? parseInt(form.compare_price_cents) : null,
      category: form.category,
      featured: form.featured,
      sort_order: parseInt(form.sort_order) || 0,
      options: { colors },
    };

    try {
      if (editing === 'new') {
        payload.slug = form.slug;
        await client.post('/api/products', payload);
      } else {
        await client.patch(`/api/products/${editing.slug}`, payload);
      }
      setEditing(null);
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.detail?.error || err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const toggleActive = async (product) => {
    if (product.active) {
      await client.delete(`/api/products/${product.slug}`);
    } else {
      await client.patch(`/api/products/${product.slug}`, { active: true });
    }
    fetchProducts();
  };

  const uploadImage = async (slug, file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await client.post(`/api/products/${slug}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchProducts();
      if (editing && editing !== 'new' && editing.slug === slug) {
        const res = await client.get(`/api/products/${slug}`);
        setEditing(res.data);
      }
    } catch {
      alert('Upload failed');
    }
    setUploading(false);
  };

  // ── Edit / Create Form ──
  if (editing) {
    const isNew = editing === 'new';
    return (
      <div style={{ maxWidth: '600px' }}>
        <button onClick={() => setEditing(null)} className="mono" style={{ background: 'none', border: 'none', color: 'var(--warm-gray)', marginBottom: '24px', cursor: 'pointer' }}>
          &larr; Back to Products
        </button>
        <h2 style={{ marginBottom: '24px' }}>{isNew ? 'New Product' : `Edit: ${editing.name}`}</h2>

        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Slug</label>
              <input className="form-input" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} required disabled={!isNew} style={!isNew ? { background: 'var(--light-gray)' } : {}} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" rows="3" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Price (cents)</label>
              <input className="form-input" type="number" value={form.price_cents} onChange={e => setForm(f => ({ ...f, price_cents: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label className="form-label">Compare Price (cents)</label>
              <input className="form-input" type="number" value={form.compare_price_cents} onChange={e => setForm(f => ({ ...f, compare_price_cents: e.target.value }))} placeholder="Optional" />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="rack">Rack</option>
                <option value="accessory">Accessory</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input className="form-input" type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
              <input type="checkbox" id="featured" checked={form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))} />
              <label htmlFor="featured" style={{ fontSize: '0.85rem' }}>Featured</label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Colors (comma-separated)</label>
            <input className="form-input" value={form.colors} onChange={e => setForm(f => ({ ...f, colors: e.target.value }))} placeholder="red, blue, cream" />
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginTop: '8px' }}>
            {saving ? 'Saving...' : isNew ? 'Create Product' : 'Save Changes'}
          </button>
        </form>

        {/* Image management (edit mode only) */}
        {!isNew && (
          <div style={{ marginTop: '32px' }}>
            <h4 style={{ marginBottom: '12px' }}>Images</h4>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {(editing.images || []).map((img, i) => (
                <div key={i} style={{ width: '100px', height: '100px', borderRadius: '6px', overflow: 'hidden', border: '2px solid var(--light-gray)' }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
              {(!editing.images || editing.images.length === 0) && (
                <p style={{ color: 'var(--warm-gray)', fontSize: '0.85rem' }}>No images yet</p>
              )}
            </div>
            <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer' }}>
              {uploading ? 'Uploading...' : 'Upload Image'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                if (e.target.files[0]) uploadImage(editing.slug, e.target.files[0]);
              }} disabled={uploading} />
            </label>
          </div>
        )}
      </div>
    );
  }

  // ── Products List ──
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>Products</h2>
        <button className="btn btn-primary btn-sm" onClick={startNew}>New Product</button>
      </div>

      {loading ? <p>Loading...</p> : products.length === 0 ? <p style={{ color: 'var(--warm-gray)' }}>No products.</p> : (
        <table className="data-table">
          <thead>
            <tr><th>Image</th><th>Name</th><th>Price</th><th>Category</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.slug} style={!p.active ? { opacity: 0.5 } : {}}>
                <td>
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt="" style={{ width: '48px', height: '48px', borderRadius: '4px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '48px', height: '48px', borderRadius: '4px', background: 'var(--light-gray)' }} />
                  )}
                </td>
                <td>
                  <div style={{ fontWeight: '600' }}>{p.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--warm-gray)' }}>{p.slug}</div>
                </td>
                <td>
                  {formatPrice(p.price_cents)}
                  {p.compare_price_cents && <span style={{ textDecoration: 'line-through', color: 'var(--warm-gray)', marginLeft: '6px', fontSize: '0.8rem' }}>{formatPrice(p.compare_price_cents)}</span>}
                </td>
                <td><span className="mono">{p.category}</span></td>
                <td>
                  <span className={`badge ${p.active ? 'badge-shipped' : 'badge-cancelled'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                  {p.featured && <span className="badge badge-processing" style={{ marginLeft: '4px' }}>Featured</span>}
                </td>
                <td style={{ display: 'flex', gap: '6px' }}>
                  <button className="btn btn-sm btn-secondary" onClick={() => startEdit(p)}>Edit</button>
                  <button className="btn btn-sm btn-secondary" onClick={() => toggleActive(p)}>
                    {p.active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════
   CUSTOMERS TAB
   ════════════════════════════════════════════════════ */
function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    client.get('/api/admin/customers')
      .then(res => setCustomers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const viewCustomer = async (cust) => {
    setDetail(cust);
    setLoadingOrders(true);
    // Fetch orders by searching for customer email
    try {
      const res = await client.get('/api/admin/orders', { params: { search: cust.email } });
      setCustomerOrders(res.data);
    } catch {
      setCustomerOrders([]);
    }
    setLoadingOrders(false);
  };

  if (detail) {
    return (
      <div>
        <button onClick={() => setDetail(null)} className="mono" style={{ background: 'none', border: 'none', color: 'var(--warm-gray)', marginBottom: '24px', cursor: 'pointer' }}>
          &larr; Back to Customers
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '32px', alignItems: 'start' }}>
          <div className="cart-summary">
            <h3 style={{ marginBottom: '16px' }}>{detail.name || 'Customer'}</h3>
            <div style={{ fontSize: '0.85rem', lineHeight: '2' }}>
              <p><span className="mono" style={{ color: 'var(--warm-gray)' }}>Email</span><br />{detail.email}</p>
              <p><span className="mono" style={{ color: 'var(--warm-gray)' }}>Username</span><br />{detail.username || '-'}</p>
              <p><span className="mono" style={{ color: 'var(--warm-gray)' }}>Orders</span><br />{detail.order_count}</p>
              <p><span className="mono" style={{ color: 'var(--warm-gray)' }}>Total Spent</span><br />{formatPrice(detail.total_spent)}</p>
              <p><span className="mono" style={{ color: 'var(--warm-gray)' }}>Joined</span><br />{new Date(detail.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          <div>
            <h3 style={{ marginBottom: '16px' }}>Order History</h3>
            {loadingOrders ? <p>Loading...</p> : customerOrders.length === 0 ? <p style={{ color: 'var(--warm-gray)' }}>No orders.</p> : (
              <table className="data-table">
                <thead>
                  <tr><th>Order</th><th>Date</th><th>Total</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {customerOrders.map(o => (
                    <tr key={o.order_number}>
                      <td><strong>{o.order_number}</strong></td>
                      <td>{new Date(o.created_at).toLocaleDateString()}</td>
                      <td>{formatPrice(o.total_cents)}</td>
                      <td><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <h2 style={{ marginBottom: '24px' }}>Customers</h2>
      {loading ? <p>Loading...</p> : customers.length === 0 ? <p style={{ color: 'var(--warm-gray)' }}>No customers yet.</p> : (
        <table className="data-table">
          <thead>
            <tr><th>Email</th><th>Name</th><th>Orders</th><th>Total Spent</th><th>Joined</th><th></th></tr>
          </thead>
          <tbody>
            {customers.map(c => (
              <tr key={c.id}>
                <td>{c.email}</td>
                <td>{c.name || '-'}</td>
                <td>{c.order_count}</td>
                <td>{formatPrice(c.total_spent)}</td>
                <td>{new Date(c.created_at).toLocaleDateString()}</td>
                <td><button className="btn btn-sm btn-secondary" onClick={() => viewCustomer(c)}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════
   STATS TAB
   ════════════════════════════════════════════════════ */
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/admin/stats')
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <p>Loading...</p>;

  const cards = [
    { label: 'Total Orders', value: stats.total_orders },
    { label: 'Revenue', value: formatPrice(stats.total_revenue_cents || 0) },
    { label: 'Customers', value: stats.total_customers },
    { label: 'Active Products', value: stats.total_products },
    { label: 'Pending Orders', value: stats.pending_orders },
  ];

  return (
    <>
      <h2 style={{ marginBottom: '24px' }}>Dashboard</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
        {cards.map(s => (
          <div key={s.label} style={{ background: 'white', borderRadius: '8px', padding: '24px', textAlign: 'center' }}>
            <p className="mono" style={{ color: 'var(--warm-gray)', marginBottom: '8px' }}>{s.label}</p>
            <p style={{ fontSize: '2rem', fontWeight: '700', fontFamily: 'var(--font-display)' }}>{s.value}</p>
          </div>
        ))}
      </div>
    </>
  );
}
